/**
 * @file bug-report-form.jsx
 * @description Bug report form component
 * @version 0.0.1
 * Created at: 30/10/2025
 * Updated at: 30/10/2025
 */
'use client';

import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Upload, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { createBugReport } from '@/actions';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/components';
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadItemProgress,
  FileUploadList,
  FileUploadTrigger,
} from '@/components/ui/file-upload';
import { authClient, responseHandler } from '@/lib';
import { fetchAPIWithFiles } from '@/lib/fetcher';
import { logger } from '@/lib/logger';
import { bugReportSchema } from '@/zod-schema';


/**
 * @file bug-report-form.jsx
 * @description Bug report form component
 * @version 0.0.1
 * Created at: 30/10/2025
 * Updated at: 30/10/2025
 */

export default function BugReportForm({ onSubmitted }) {
  //-------------- State & Variables --------------//

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const { data: userData } = authClient.useSession();
  const [uploadStatus, setUploadStatus] = React.useState({
    isUploading: false,
    error: null,
    success: false,
  });

  const form = useForm({
    resolver: zodResolver(bugReportSchema),
    defaultValues: {
      title: '',
      description: '',
      email: userData?.user?.email?.toLowerCase() || '',
      type: 'bug',
      images: [],
    },
  });

  //-------------- Use Effects --------------//

  const onUpload = React.useCallback(async (uploadFiles, { onProgress, onSuccess, onError }) => {
    const uploadPromises = uploadFiles.map(async (file) => {
      try {
        setUploadStatus({
          isUploading: true,
          error: null,
          success: false,
        });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'bug_reports/images');

        const res = await fetchAPIWithFiles('/upload', {
          method: 'POST',
          formData,
          onUploadProgress: (event) => {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(file, percent);
          },
        });

        if (res.status_code === 200 && res.data?.status) {
          const fileUrl = res.data.data?.blob_name;
          if (fileUrl) {
            onSuccess(file);
            // Store the URL in a custom property on the file
            file.uploadedUrl = fileUrl;
          }
        } else {
          setFiles(files.filter((f) => f.name !== file.name));
          toast.error(res.data?.message || 'Upload failed');
        }
      } catch (error) {
        setFiles(files.filter((f) => f.name !== file.name));
        console.error('Upload error:', error);
        onError(file, error instanceof Error ? error : new Error('Upload failed'));
        toast.error(`Failed to upload ${file.name}`);
        setUploadStatus({
          isUploading: false,
          error: error instanceof Error ? error : new Error('Upload failed'),
          success: false,
        });
      }
    });

    await Promise.all(uploadPromises).finally(() => {
      setUploadStatus({
        isUploading: false,
        error: null,
        success: true,
      });
    });
  }, []);

  //-------------- Other Methods --------------//
  const onFileReject = React.useCallback((file, message) => {
    toast.warning(message, { description: `"${file.name}" was rejected.` });
  }, []);

  const onSubmit = async (values) => {
    console.log(values);
    try {
      setIsSubmitting(true);
      // Extract uploaded URLs from files that were successfully uploaded
      const uploadedUrls = files.filter((f) => f.uploadedUrl).map((f) => f.uploadedUrl);

      const payload = { ...values, image: uploadedUrls, reporter_email: values.email };
      const api_call = createBugReport(payload);

      const res = await responseHandler(api_call, 'Bug submit successfully.', 'Bug submitting...');

      if (res?.status) {
        form.reset();
        onSubmitted?.();
      }
    } catch (error) {
      logger.error('Error submitting bug report', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
        {/* Title */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Short summary" disabled={isSubmitting} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Title */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Email address" disabled={isSubmitting} {...field} onChange={(e) => field.onChange(e.target.value.toLowerCase())} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Type */}
        {/* <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                                <FormControl>
                                    <SelectTrigger className="w-full" size='lg'>
                                        <SelectValue placeholder="Select report type" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {BUG_OPTIONS.map((t) => <SelectItem value={t.value}>{t.icon} {t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                /> */}

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  rows={5}
                  placeholder="Describe in detail..."
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Upload */}
        <div>
          <FormLabel>Upload Images</FormLabel>
          <FileUpload
            value={files}
            onValueChange={setFiles}
            onUpload={onUpload}
            onFileReject={onFileReject}
            maxFiles={3}
            multiple
            accept="image/png, image/jpg, image/jpeg"
            maxSize={5 * 1024 * 1024}
            className="w-full"
          >
            <FileUploadDropzone className="mt-3">
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="flex items-center justify-center rounded-full border p-2.5">
                  <Upload className="text-muted-foreground size-6" />
                </div>
                <p className="text-sm font-medium">Drag & drop files here</p>
                <p className="text-muted-foreground text-xs">Or click to browse (max 3 files)</p>
              </div>
              <FileUploadTrigger asChild>
                <Button variant="outline" size="sm" className="mt-2 w-fit">
                  Browse files
                </Button>
              </FileUploadTrigger>
            </FileUploadDropzone>

            <FileUploadList>
              {files.map((file, index) => (
                <FileUploadItem key={index} value={file} className="p-0">
                  <FileUploadItemPreview className="aspect-square size-20 max-h-20 object-contain">
                    <FileUploadItemProgress variant="circular" />
                  </FileUploadItemPreview>
                  <FileUploadItemMetadata className="sr-only" />
                  <FileUploadItemDelete asChild>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute -top-1 -right-1 size-5 rounded-full"
                    >
                      <X className="size-3" />
                    </Button>
                  </FileUploadItemDelete>
                </FileUploadItem>
              ))}
            </FileUploadList>
          </FileUpload>
        </div>
        {uploadStatus.error && <p className="text-red-500">{uploadStatus.error}</p>}

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting || uploadStatus.error || !uploadStatus.success}
          isLoading={isSubmitting}
          className="mx-auto w-fit"
        >
          Submit Report
        </Button>
      </form>
    </Form>
  );
}
